import { Card, Drawer, Text } from '@mantine/core'
import { CodeHighlight } from '@mantine/code-highlight'
import {
  TSnmpPacket,
  TSnmpPacketList,
  useGetSnmpPackets,
} from 'api/packets/getSnmpPacketList'
import { CodeBox, QueryPaginated } from 'components/common'
import Datatable, { TDatatable } from 'components/datatable'
import { DatatableProps } from 'components/datatable/Datatable'
import { r } from 'config/routes'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiRedPacketLine } from 'react-icons/ri'
import { useParams } from 'react-router-dom'
import { drawerStyles } from 'utils/drawerStyles'

const headers: (t: TFunction) => TDatatable<TSnmpPacket>[] = t => [
  {
    id: 'time',
    label: t('generic.time'),
    colSize: 1,
    selector: item => (
      <Text>{dayjs(item.date).format('DD.MM.YYYY, HH:mm:ss')}</Text>
    ),
  },
  {
    id: 'oid',
    label: 'OID',
    colSize: 2,
    selector: item => <CodeBox value={item.oid} />,
  },
  {
    id: 'data',
    label: t('generic.data'),
    colSize: 8,
    selector: item => (
      <Text style={{ fontFamily: 'monospace' }} truncate>
        {item.value}
      </Text>
    ),
  },
]

export const SnmpDevicePacketList = () => {
  const { t } = useTranslation()
  const { deviceId } = useParams()
  const query = useGetSnmpPackets({ deviceId })

  return (
    <Card>
      <QueryPaginated.Wrapper query={query}>
        <QueryPaginated.Data>
          {(data: TSnmpPacketList) => <SnmpPacketTable data={data.items} />}
        </QueryPaginated.Data>
        <QueryPaginated.NoData
          icon={RiRedPacketLine}
          title={t('packet.noData')}
          description={t('sensor.checkConfigurationCta')}
          buttonContent={t('sensor.checkConfiguration')}
          to={r['snmpDevices.edit'](deviceId, 'oids')}
        />
      </QueryPaginated.Wrapper>
    </Card>
  )
}

export const SnmpPacketTable = ({
  data,
  ...props
}: { data: TSnmpPacket[] } & Partial<
  Omit<DatatableProps<TSnmpPacket>, 'data' | 'headers' | 'onClick'>
>) => {
  const { t } = useTranslation()
  const [drawer, setDrawer] = useState<TSnmpPacket | null>(null)

  return (
    <>
      <Datatable
        headers={headers(t)}
        onClick={item => setDrawer(item)}
        data={data}
        {...props}
      />
      <Drawer
        styles={drawerStyles}
        padding={'lg'}
        position="right"
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        title={t('packet.eventDetails')}
      >
        <CodeHighlight
          withLineNumbers
          language="json"
          copyLabel={t('generic.copy')!}
          copiedLabel={t('generic.copied')!}
          code={JSON.stringify(drawer, null, 2)}
        />
      </Drawer>
    </>
  )
}
